/* eslint-disable no-unexpected-multiline */

import React from 'react';
import styled, { keyframes } from 'styled-components';
import { black } from '@spotify-internal/encore-foundation';

type BarAnimationProps = {
  barHeight: number;
  barSpacing: number;
  timing: number;
  width: number;
  color: string;
  isPlaying: boolean;
};

const barHeightAnimation = keyframes`
  0% { transform: scale(1,0.1); }
  100% { transform: scale(1,15); }
`;

function barWidthCalculation(value: number): number {
  return value / 4;
}

function barLeftPosition(
  barWidth: number,
  barSpacing: number,
  childIndex: number,
): number {
  const firstValue = barWidthCalculation(barWidth) + barSpacing;
  return firstValue * childIndex;
}

function barContainerWidth(width: number, spacing: number): number {
  const totalSpacing = spacing * 3;
  return width + totalSpacing;
}

const BarAnimationContainer = styled.div<{
  barHeight: number;
  width: number;
  barSpacing: number;
}>`
  ${props => `height: ${props.barHeight}px`};
  overflow: hidden;
  position: relative;
  ${props => `width: ${barContainerWidth(props.width, props.barSpacing)}px`};
`;

const BarAnimationBar = styled.span<{
  timing: number;
  color: string;
  width: number;
  barHeight: number;
  barSpacing: number;
  isPlaying: boolean;
}>`
  animation-direction: alternate;
  ${props => `animation-duration: ${props.timing}ms`};
  animation-iteration-count: infinite;
  ${props => `animation-play-state: ${props.isPlaying ? 'running' : 'paused'}`};
  animation-timing-function: linear;
  ${props => `background-color: ${props.color}`};
  bottom: 0;
  display: inline-block;
  height: 1px;
  position: absolute;
  transform-origin: center bottom;
  ${props => `width: ${barWidthCalculation(props.width)}px`};

  &:nth-child(1) {
    animation-delay: 0ms;
    animation-name: ${barHeightAnimation};
    left: 0;
  }

  &:nth-child(2) {
    ${props => `animation-delay: ${props.timing / 1.66}ms`};
    animation-name: ${barHeightAnimation};
    ${props => `left: ${barLeftPosition(props.width, props.barSpacing, 1)}px`};
  }

  &:nth-child(3) {
    ${props => `animation-delay: ${props.timing / 0.83}ms`};
    animation-name: ${barHeightAnimation};
    ${props => `left: ${barLeftPosition(props.width, props.barSpacing, 2)}px`};
  }

  &:nth-child(4) {
    ${props => `animation-delay: ${props.timing / 0.55}ms`};
    animation-name: ${barHeightAnimation};
    ${props => `left: ${barLeftPosition(props.width, props.barSpacing, 3)}px`};
  }
`;

BarAnimation.defaultProps = {
  width: 12,
  barSpacing: 3,
  barHeight: 20,
  color: `${black}`,
  timing: 450,
};

export function BarAnimation(props: BarAnimationProps) {
  const { barHeight, width, barSpacing, timing, color, isPlaying } = props;

  return (
    <BarAnimationContainer
      barHeight={barHeight}
      barSpacing={barSpacing}
      width={width}
      data-testid="bar-animation"
    >
      <BarAnimationBar
        barHeight={barHeight}
        timing={timing}
        color={color}
        barSpacing={barSpacing}
        width={width}
        data-testid="bar-animation-bar"
        isPlaying={isPlaying}
      />
      <BarAnimationBar
        barHeight={barHeight}
        timing={timing}
        color={color}
        barSpacing={barSpacing}
        width={width}
        isPlaying={isPlaying}
      />
      <BarAnimationBar
        barHeight={barHeight}
        timing={timing}
        color={color}
        barSpacing={barSpacing}
        width={width}
        isPlaying={isPlaying}
      />
      <BarAnimationBar
        barHeight={barHeight}
        timing={timing}
        color={color}
        barSpacing={barSpacing}
        width={width}
        isPlaying={isPlaying}
      />
    </BarAnimationContainer>
  );
}
